'use client';
import { tracking } from '@/lib/features/tracking';

// Marks this as a client-side component in Next.js

/**
 * Error Boundary Component for Next.js applications
 * This component handles and displays runtime errors that occur in the application
 *
 * @param {Object} props - Component props
 * @param {Error & {digest?: string}} props.error - The error object containing details about what went wrong
 * @param {() => void} props.reset - Function provided by Next.js to attempt to recover from the error
 *
 * @returns {JSX.Element} A user-friendly error page with a retry button
 */
function Error({ error, reset }: { error?: Error & { digest?: string }; reset: () => void }) {
    // Log the error to console for debugging purposes
    tracking.error(error as Error, {
        source: 'error.tsx',
    });

    return (
        // Container div with full viewport height and centered content
        <div className="flex min-h-screen flex-col items-center justify-center">
            {/* Error message header */}
            <h2 className="mb-4 text-2xlarge font-bold">Something went wrong!</h2>
            {/* Reset button that attempts to recover from the error */}
            <button
                className="bg-info rounded px-4 py-2 text-white hover:bg-primary"
                onClick={() => reset()}
            >
                Try again
            </button>
        </div>
    );
}

// Set a display name for better debugging in React DevTools
Error.displayName = 'ErrorPage';

// Export the component as the default export
export default Error;
